import { useEffect, useState, type ReactNode } from 'react';

import { EmptyState } from '../components/EmptyState.js';
import { retryLiveInstall, useLiveError, useLiveReady } from '../data/source.js';
import { ensureReauth } from '../lib/session.js';
import { useSession } from './SessionProvider.js';
import { useStreamClient } from './StreamProvider.js';
import styles from './AuthGate.module.css';

/**
 * How long to wait for a host handoff before deciding the embed is unauthorized.
 * A real embed reaches a session in ~1-2s (ready → init → redeem → connect); this
 * only elapses when no handoff ever arrives — e.g. the URL was opened standalone.
 */
const GRACE_MS = 8000;

type AuthState = 'connecting' | 'authorized' | 'unauthorized' | 'expired' | 'unavailable' | 'error';

type Decision = { state: AuthState; retry: (() => void) | null };

const COPY: Record<Exclude<AuthState, 'authorized'>, { title: string; description: string }> = {
  connecting: { title: 'Connecting…', description: 'Setting up your chat.' },
  expired: { title: 'Session expired', description: 'Sign in to ajobthing again, then retry.' },
  unavailable: {
    title: 'Couldn’t connect to chat',
    description: 'Retry now, or try again in a moment.',
  },
  error: {
    title: 'Couldn’t load your chats',
    description: 'Something went wrong while loading. Please try again.',
  },
  unauthorized: {
    title: 'Not authorized',
    description: 'Open chat from your ajobthing dashboard to continue.',
  },
};

const reauth = () => void ensureReauth();
const reloadLive = () => void retryLiveInstall();

function decide(
  session: ReturnType<typeof useSession>,
  stream: ReturnType<typeof useStreamClient>,
  liveReady: boolean,
  liveError: boolean,
  graceExpired: boolean,
): Decision {
  // Real data ready → show it (implies a valid session + connected Stream).
  if (liveReady) return { state: 'authorized', retry: null };
  // The host could not renew the login; only a fresh handoff brings this back.
  if (session.status === 'expired') return { state: 'expired', retry: reauth };
  // A refused redeem worth a fresh handoff, or a Stream connect that failed.
  if (session.status === 'unavailable') return { state: 'unavailable', retry: reauth };
  if (stream.status === 'error') return { state: 'unavailable', retry: stream.retry };
  // An invalid handoff is terminal → not authorized.
  if (session.status === 'error') return { state: 'unauthorized', retry: null };
  // Connected fine but the channel query failed → recoverable, offer a retry.
  if (liveError) return { state: 'error', retry: reloadLive };
  // Session established (or Stream mid-connect) but data not loaded yet → wait.
  if (session.sessionReady || stream.status === 'connecting') {
    return { state: 'connecting', retry: null };
  }
  // Still idle: give the handoff a grace window before calling it unauthorized.
  return { state: graceExpired ? 'unauthorized' : 'connecting', retry: null };
}

function useEmbedAuthState(): Decision {
  const session = useSession();
  const stream = useStreamClient();
  const liveReady = useLiveReady();
  const liveError = useLiveError();
  const [graceExpired, setGraceExpired] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setGraceExpired(true), GRACE_MS);
    return () => clearTimeout(timer);
  }, []);

  const decision = decide(session, stream, liveReady, liveError, graceExpired);
  const { retry } = decision;

  // One attempt per return of the tab or the network; no timer loop against the host mint.
  useEffect(() => {
    if (!retry) return;
    const onVisible = () => {
      if (!document.hidden) retry();
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('online', retry);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('online', retry);
    };
  }, [retry]);

  return decision;
}

/**
 * Gates the real embed surfaces (`/chat`, the widget panel) on session state:
 * a connecting placeholder, the real UI once live data is ready, and otherwise a
 * screen that names the failure, with Retry on every recoverable one. Only an
 * invalid handoff (or a standalone open) reads "Not authorized". Never shows fixtures.
 */
export function AuthGate({ children }: { children: ReactNode }) {
  const { state, retry } = useEmbedAuthState();

  if (state === 'authorized') return <>{children}</>;

  return (
    <div className={styles.screen}>
      <EmptyState
        {...COPY[state]}
        action={retry ? { label: 'Retry', onClick: retry } : undefined}
      />
    </div>
  );
}
